import { LoadingDots } from "@/components/loaders/LoadingDots";
import React, { ForwardedRef, forwardRef, useCallback, useState } from "react";
import { classNames } from "utils/classnames";
import { isDefined } from "@/utils/function-utils";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { Icon } from "@/components/icons/Icon";
import { DescMethod } from "@bufbuild/protobuf";

export const ButtonKinds = ["full", "flat", "outline", "text"] as const;
export const ButtonColors = [
  "primary",
  "secondary",
  "danger",
  "danger-subdued",
  "warning",
  "white",
  "accent-lime",
] as const;
export const ButtonSizes = ["xxs", "xs", "sm", "md", "lg", "xl"] as const;
export type ButtonColor = (typeof ButtonColors)[number];
export type ButtonKind = (typeof ButtonKinds)[number];
export type ButtonSize = (typeof ButtonSizes)[number];

const kindClasses: Record<ButtonKind, string> = {
  full: "button-full",
  flat: "button-flat",
  outline: "button-outline",
  text: "button-text",
};

const disabledKindClasses: Record<ButtonKind, string> = {
  full: "button-full-disabled",
  flat: "button-flat-disabled",
  outline: "button-outline-disabled",
  text: "button-text-disabled",
};

const sizeClasses: Record<ButtonSize, string> = {
  xxs: "px-2.5 py-1 text-xxs font-medium",
  xs: "px-2.5 py-1 text-xs font-medium",
  sm: "px-3 py-1.5 text-xs font-medium",
  md: "px-cds-10 py-1.5 text-cds-sm font-medium",
  lg: "px-6 py-2 text-base font-medium",
  xl: "px-6 py-2 text-cds-lg font-medium",
};

const colorClasses: Record<ButtonColor, string> = {
  primary: "button-primary",
  secondary: "button-secondary",
  danger: "button-danger",
  "danger-subdued": "focus:ring-cds-bright-green-400",
  warning: "button-warning",
  white: "button-white",
  "accent-lime": "button-accent-lime",
};

export const buttonColorKindClasses: Record<ButtonColor, Record<ButtonKind, string>> = {
  primary: {
    full: "button-primary-full",
    flat: "button-primary-flat",
    outline: "button-primary-outline",
    text: "button-primary-text",
  },
  "accent-lime": {
    full: "button-secondary-lime-full",
    flat: "button-secondary-lime-flat",
    outline: "button-secondary-lime-outline",
    text: "button-secondary-lime-text",
  },
  secondary: {
    full: "button-secondary-full",
    flat: "button-secondary-flat",
    outline: "button-secondary-outline",
    text: "border-0 text-zinc-600 hover:text-zinc-700 active:text-zinc-800 hover:bg-zinc-600/5 active:bg-zinc-600/10",
  },
  danger: {
    full: "button-danger-full",
    flat: "button-danger-flat",
    outline: "button-danger-outline",
    text: "button-danger-text",
  },
  "danger-subdued": {
    full: "border-zinc-300 text-zinc-700 fill-zinc-500 bg-white hover:bg-red-700 focus:bg-red-700 active:bg-red-800 focus:text-white focus:fill-white hover:text-white hover:fill-white active:fill-white active:text-white hover:border-red-800",
    flat: "border-zinc-300 text-zinc-700 fill-zinc-500 bg-white hover:bg-red-700 focus:bg-red-700 active:bg-red-800 focus:text-white focus:fill-white hover:text-white hover:fill-white active:fill-white active:text-white hover:border-red-800",
    outline:
      "border-zinc-300 text-zinc-600 fill-zinc-600 focus:text-red-700 focus:border-red-300 focus:fill-red-600 hover:text-red-700 hover:border-red-300 hover:fill-red-600 active:text-red-800 active:bg-red-600/10 hover:border-red-700 active:border-red-800",
    text: "text-zinc-600 focus:text-red-700 focus:fill-red-700 focus:bg-red-600/5 hover:text-red-700 hover:fill-red-700  hover:bg-red-600/5 active:text-red-800 active:bg-red-600/10",
  },
  warning: {
    full: "button-warning-full",
    flat: "button-warning-flat",
    outline: "button-warning-outline",
    text: "button-warning-text",
  },
  white: {
    full: "button-white-full",
    flat: "button-white-flat",
    outline: "button-white-outline",
    text: "button-white-text",
  },
};

const loadingColorKindClasses: Record<ButtonColor, Record<ButtonKind, string>> = {
  primary: {
    full: "button-primary-full-loading",
    flat: "button-primary-flat-loading",
    outline: "button-primary-outline-loading",
    text: "button-primary-text-loading",
  },
  "accent-lime": {
    full: "button-secondary-lime-full-loading",
    flat: "button-secondary-lime-flat-loading",
    outline: "button-secondary-lime-outline-loading",
    text: "button-secondary-lime-text-loading",
  },
  secondary: {
    full: "button-secondary-full-loading",
    flat: "button-secondary-flat-loading",
    outline: "button-secondary-outline-loading",
    text: "button-secondary-text-loading",
  },
  danger: {
    full: "button-danger-full-loading",
    flat: "button-danger-flat-loading",
    outline: "button-danger-outline-loading",
    text: "button-danger-text-loading",
  },
  "danger-subdued": {
    full: "shadow-inner-md bg-red-800",
    flat: "shadow-inner bg-red-800",
    outline: "button-danger-outline-loading",
    text: "button-danger-text-loading",
  },
  warning: {
    full: "button-warning-full-loading",
    flat: "button-warning-flat-loading",
    outline: "button-warning-outline-loading",
    text: "button-warning-text-loading",
  },
  white: {
    full: "button-white-full-loading",
    flat: "button-white-flat-loading",
    outline: "button-white-outline-loading",
    text: "button-white-text-loading",
  },
};

interface SharedButtonProps {
  block?: boolean;
  color?: ButtonColor;
  kind?: ButtonKind;
  loading?: boolean;
  size?: ButtonSize;
  hideFocus?: boolean;
}

export type ButtonProps = SharedButtonProps & Omit<React.ComponentProps<"button">, "ref">;

export const Button = forwardRef(function ButtonImpl(
  {
    type = "button",
    children,
    className,
    disabled,
    block,
    color = "secondary",
    kind = "full",
    loading: loadingFromProps,
    hideFocus,
    size = "sm",
    onClick,
    ...rest
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const [loadingFromState, setLoadingFromState] = useState(false);

  const wrappedOnClick = useCallback<Required<React.ComponentProps<"button">>["onClick"]>(
    (...args) => {
      const res = onClick?.(...args);
      if (typeof res !== "object" || !isDefined(res)) {
        return;
      }
      if ("then" in res && typeof res === "function") {
        setLoadingFromState(true);
        Promise.resolve(res).finally(() => setLoadingFromState(false));
      }
    },
    [onClick],
  );

  const loading = loadingFromProps || loadingFromState;

  return (
    <button
      ref={ref}
      className={classNames(
        "button",
        !hideFocus && "button-focusable",
        sizeClasses[size],
        kindClasses[kind],
        colorClasses[color],
        buttonColorKindClasses[color][kind],
        block ? "flex" : "inline-flex",
        loading && "button-loading",
        loading && loadingColorKindClasses[color][kind],
        disabled && disabledKindClasses[kind],
        className,
      )}
      disabled={disabled || loading}
      {...rest}
      onClick={wrappedOnClick}
      type={type}
    >
      {children}
      {loading && (
        <span className={classNames("absolute inset-0 flex justify-center items-center")}>
          <LoadingDots size={size} />
        </span>
      )}
    </button>
  );
});

export interface LinkButtonProps
  extends SharedButtonProps,
    Omit<React.ComponentProps<"a">, "ref" | "color"> {
  href: string;
  noIcon?: boolean;
}

export const LinkButton = forwardRef(function LinkButtonImpl(
  {
    children,
    className,
    block,
    color = "secondary",
    kind = "full",
    hideFocus,
    size = "sm",
    onClick,
    target,
    noIcon,
    ...rest
  }: LinkButtonProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  return (
    <ChalkLink
      unstyled
      ref={ref}
      className={classNames(
        "button",
        !hideFocus && "button-focusable",
        sizeClasses[size],
        kindClasses[kind],
        colorClasses[color],
        buttonColorKindClasses[color][kind],
        block ? "flex" : "inline-flex",
        className,
      )}
      openInNewTab={target === "_blank"}
      showOpenInNewTabIcon={!noIcon}
      {...rest}
    >
      {children}
      {!noIcon && target !== "_blank" && <Icon name="arrowRight" size={size} />}
    </ChalkLink>
  );
});

export interface DivButtonProps
  extends SharedButtonProps,
    Omit<React.ComponentProps<"div">, "ref" | "color"> {
  disabled?: boolean;
}

export const DivButton = forwardRef(function ButtonImpl(
  {
    children,
    className,
    disabled,
    block,
    color = "secondary",
    kind = "full",
    loading,
    hideFocus,
    size = "sm",
    ...rest
  }: DivButtonProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      className={classNames(
        "button",
        !hideFocus && "button-focusable",
        sizeClasses[size],
        kindClasses[kind],
        colorClasses[color],
        buttonColorKindClasses[color][kind],
        block ? "flex" : "inline-flex",
        loading && "button-loading",
        loading && loadingColorKindClasses[color][kind],
        disabled && disabledKindClasses[kind],
        className,
      )}
      {...rest}
    >
      {children}
      {loading && (
        <span className={classNames("absolute inset-0 flex justify-center items-center")}>
          <LoadingDots size={size} />
        </span>
      )}
    </div>
  );
});
